import React, { useEffect, useState } from 'react';
import { Card, Typography, Divider, Table, Row, Col, Form, Select, Input, DatePicker, Button, message } from 'antd';
import { useWindowDimensions } from '../utils/hooks';
import { useRouteMatch, useParams, useHistory } from 'react-router';
import { States, State } from 'sigt';
import { connect, useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { CreditCardOutlined } from '@ant-design/icons';
import {
  changeProcedureCertificate,
  changeProcedureData,
  changeProcedureStatus,
  updateProcedure,
} from '../redux/actions/procedures';
import BillTable from './OrdinanceBill/BillTable';
import { formatCurrency } from '../utils/formatNumber';
import MultipleTransactions from './Forms/components/MultipleTransactions';
import MultipleWireTransfers from './Forms/components/MultipleWireTransfers';
import Axios from 'axios';
import { cloneDeep } from 'lodash';
const server = process.env.REACT_APP_SERVER_URL;

const PayProcedure: React.FC<PayProcedureProps> = ({ thm, prcd, banks, institutions, auth, updateProcedure }) => {
  useRouteMatch('/dashboard/pagarTramite/:idTramite');
  const { width } = useWindowDimensions();
  const { idTramite } = useParams<{ idTramite: string }>();
  const selectedPrcd = prcd.procedures.find((p) => p.id === parseInt(idTramite || '0'));
  const selectedInstitution = institutions?.find((i) => i.tramitesDisponibles?.find((t) => t.id === selectedPrcd?.tipoTramite));
  const selectedPrcdClass = selectedInstitution?.tramitesDisponibles?.find((t) => t.id === selectedPrcd?.tipoTramite);
  const [form] = Form.useForm();
  const history = useHistory();
  const [loading, setLoading] = useState<boolean>(false);
  const cashierSedemat: number[] = [22, 25, 40, 41];
  const vhc = useSelector((state: State) => state.vhc);
  const PETRO = useSelector((state: State) => state.coin.petro);
  const [comprobantes, setComprobantes] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const dispatch = useDispatch();

  useEffect(() => {
    if (!cashierSedemat.includes(auth.user?.institucion?.cargo?.id || 0) && auth.user?.tipoUsuario !== 4) {
      history.push('/dashboard');
    }
    // eslint-disable-next-line
  }, [auth.user]);

  useEffect(() => {
    if (prcd.procedures.length > 0 && !selectedPrcd) {
      history.push('/dashboard');
    }
    // eslint-disable-next-line
  }, [prcd]);

  const disabledDate = (current) => current > moment().endOf('day');

  const handleBankChange = (banco) => {
    form.setFieldsValue({
      banco,
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (comprobantes?.length === 0 && auth?.user?.tipoUsuario === 4) {
        return message.error('Debe subir el comprobante de pago.');
      }

      const values = await form.validateFields();
      Object.keys(values).forEach((k) => {
        if (moment.isMoment(values[k])) {
          values[k] = values[k].toISOString();
        }
      });

      const pagos = Array.isArray(form.getFieldValue('pagos')) ? cloneDeep(form.getFieldValue('pagos')) || [] : [];

      pagos
        .filter((p) => p)
        .forEach((p) => {
          Object.keys(p).forEach((k) => {
            if (moment.isMoment(p[k])) {
              p[k] = p[k].toISOString();
            }
          });
        });

      const montoAPagar =
        (getAllVHCategories()?.find((c) => c.id === selectedPrcd?.datos?.funcionario?.vehiculo?.categoria?.id)?.tarifa ?? 0) *
        PETRO;

      if (pagos.map((p) => p.costo).reduce((c, v) => c + v, 0) < montoAPagar.toFixed(2)) {
        return message.error('La suma de los montos de las transacciones debe ser igual al total a pagar.');
      }

      delete values.pago;

      const tramite = {
        estado: selectedPrcd?.estado === 'pagocajero' ? 'pagocajero' : 'ingresardatos',
        pago: pagos,
        idTramite: parseInt(idTramite || '0'),
        tipoTramite: selectedPrcdClass?.id,
      };

      const response = await Axios.put(
        `${server}/procedures/update`,
        { tramite, idUsuario: auth.user?.id },
        {
          headers: { Authorization: `Bearer ${auth.token}` },
        }
      );

      if (![200, 201].includes(response.data.status)) throw new Error('Error al momento de procesar vehiculo');

      const fd = new FormData();
      if (comprobantes) {
        comprobantes.forEach((e) => fd.append('comprobantesTramites', e.file));

        const responseFile = await Axios.post(`${server}/uploads/comprobantesTramites/${response.data.tramite.id}`, fd, {
          headers: { Authorization: `Bearer ${auth.token}` },
        });
        if (responseFile.status !== 200) message.error('Error al subir los comprobantes');
      }

      dispatch(changeProcedureStatus(response.data.tramite.id, response.data.tramite.estado));
      if (response.data.tramite.certificado)
        dispatch(changeProcedureCertificate(response.data.tramite.id, response.data.tramite.certificado));
      if (response.data.tramite.tipoTramite === 26)
        dispatch(changeProcedureData(response.data.tramite.id, response.data.tramite));

      setLoading(false);
      history.push('/dashboard/bandejas');
      message.success('Pago registrado de manera exitosa.');
    } catch (e) {
      message.error(
        (e?.response?.data?.message as string)?.includes('referencia')
          ? 'La referencia bancaria ya ha sido utilizada.'
          : 'Error en el servidor'
      );
    } finally {
      setLoading(false);
    }
  };

  const columns: any = [
    {
      title: 'Banco',
      dataIndex: 'banco',
      key: 'banco',
      fixed: 'left',
    },
    {
      title: 'Numero de Cuenta',
      dataIndex: 'numeroCuenta',
      key: 'numeroCuenta',
    },
    {
      title: 'Titular',
      dataIndex: 'nombreTitular',
      key: 'nombreTitular',
    },
    {
      title: 'Documento de Identidad',
      dataIndex: 'documento',
      key: 'documento',
    },
  ];

  const getAllVHCategories = () => {
    return vhc.types.map((c) => c.categorias.map((c) => c)).flat();
  };

  const addVoucher = (e) => {
    setComprobantes([...comprobantes, e]);
  };

  const removeVoucher = (e) => {
    let newComprobantes = comprobantes.filter((f) => f.file.name !== e);
    setComprobantes(newComprobantes);
  };

  return (
    <Card
      style={{ height: '100%' }}
      title='Pagar Trámite'
      bodyStyle={{ height: 'calc(100% - 88px)', overflowY: 'scroll', overflowX: 'hidden' }}
      headStyle={{ height: 64, backgroundColor: thm.primaryColor, padding: width < 992 ? '0 10px' : '0 20px', color: 'white' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography.Text strong>Codigo de Tramite: {selectedPrcd?.codigoTramite}</Typography.Text>
        <Typography.Text strong>Fecha: {moment(selectedPrcd?.fechaCreacion).format('DD-MM-YYYY')}</Typography.Text>
      </div>
      <Form
        form={form}
        layout='vertical'
        style={{ padding: width < 992 ? '0 10px' : '0px 20px' }}
        onFinish={handleSubmit}
        onValuesChange={(c, v) => {
          form.setFieldsValue(v);
          setData(v);
        }}
      >
        <Divider orientation='left' style={{ marginLeft: -30, marginBottom: 0 }}>
          <Typography.Title level={4}>Datos de Pago</Typography.Title>
        </Divider>
        {/* tramite de VH */}
        {selectedPrcd?.tipoTramite === 39 ? (
          <Typography.Text strong>
            El monto de pago del impuesto vehicular tiene un total de{' '}
            <Typography.Text strong type='danger'>
              Bs.{' '}
              {formatCurrency(
                (getAllVHCategories()?.find((c) => c.id === selectedPrcd?.datos?.funcionario?.vehiculo?.categoria?.id)?.tarifa ??
                  0) * PETRO
              )}
              . equivalente a{' '}
              {formatCurrency(
                getAllVHCategories()?.find((c) => c.id === selectedPrcd?.datos?.funcionario?.vehiculo?.categoria?.id)?.tarifa ?? 0
              )}{' '}
              MMV{' '}
            </Typography.Text>
            El monto de la transferencia debe coincidir exactamente con el costo del impuesto vehicular y debe ser realizada desde
            el mismo banco.
          </Typography.Text>
        ) : (
          <Typography.Text strong type='danger'>
            El monto del pago debe coincidir exactamente con el costo del trámite.
          </Typography.Text>
        )}

        {/* <BillTable data={selectedPrcd?.bill?.items || []} /> */}
        <Divider />
        {/* {selectedPrcd?.estado !== 'pagocajero' ? (
          <>
            <Typography.Text strong type='danger'>
              El monto de la transferencia debe coincidir exactamente con el costo del tramite y debe ser realizada desde el mismo
              banco.
            </Typography.Text>
            <Table
              bordered
              style={{ margin: '20px 0px' }}
              pagination={false}
              columns={columns}
              dataSource={selectedInstitution?.cuentasBancarias?.map((c) => ({
                ...c,
                documento: `${c.rif ? 'RIF: ' : 'Cedula: '}${c.rif || c.cedula}`,
                banco: banks?.find((b) => b.id === c.banco)?.nombre,
                key: c.id,
              }))}
            />
          </>
        ) : (
          <Typography.Text strong type='danger'>
            El monto del pago debe coincidir exactamente con el costo del trámite.
          </Typography.Text>
        )} */}
        {auth.user?.tipoUsuario !== 4 ? (
          <MultipleTransactions selectedInstitution={institutions?.find((inst) => inst.id === 9)} form={form} data={data} />
        ) : (
          <MultipleWireTransfers
            selectedInstitution={institutions?.find((inst) => inst.id === 9)}
            form={form}
            addVoucher={(e) => addVoucher(e)}
            removeVoucher={(e) => removeVoucher(e)}
          />
        )}
        {/* <Row gutter={24}>
          <Col xs={24} xl={8}>
            <Form.Item
              label={selectedPrcd?.estado === 'pagocajero' ? 'Banco Origen' : 'Banco'}
              name='banco'
              rules={[{ required: true, message: 'Debe indicar el banco' }]}
            >
              {banks.length > 0 ? (
                <Select
                  placeholder={selectedPrcd?.estado === 'pagocajero' ? 'Banco Origen' : 'Banco'}
                  onChange={handleBankChange}
                >
                  {selectedPrcd?.estado !== 'pagocajero'
                    ? banks
                        .filter((b) => selectedInstitution?.cuentasBancarias?.map((c) => c.banco).includes(b.id))
                        .map((b, i) => (
                          <Select.Option key={`b-${b.id}`} value={b.id}>
                            {b.nombre}
                          </Select.Option>
                        ))
                    : banks.map((b) => (
                        <Select.Option key={`b-${b.id}`} value={b.id}>
                          {b.nombre}
                        </Select.Option>
                      ))}
                </Select>
              ) : (
                <div></div>
              )}
            </Form.Item>
          </Col>
          <Col xs={24} xl={8}>
            <Form.Item
              label='Numero de Referencia'
              name='referencia'
              rules={[{ required: true, message: 'Por favor ingrese el numero de referencia.' }]}
            >
              <Input placeholder='Referencia' />
            </Form.Item>
          </Col>
          {selectedPrcd?.estado === 'pagocajero' && (
            <Col xs={24} xl={8}>
              <Form.Item label='Banco de Destino' name='destino' rules={[{ required: true, message: 'Debe indicar el banco' }]}>
                {banks.length > 0 ? (
                  <Select placeholder='Banco de Destino'>
                    {banks?.map((b, i) => (
                      <Select.Option key={`b-${b.id}`} value={b.id}>
                        {b.nombre}
                      </Select.Option>
                    ))}
                  </Select>
                ) : (
                  <div></div>
                )}
              </Form.Item>
            </Col>
          )}
          <Col xs={24} xl={8}>
            <Form.Item
              label='Fecha de Pago'
              name='fecha'
              rules={[{ required: true, message: 'Por favor ingrese la fecha de pago.' }]}
            >
              <DatePicker style={{ width: '100%' }} format={'DD-MM-YYYY'} disabledDate={disabledDate} />
            </Form.Item>
          </Col>
        </Row> */}
      </Form>
      <Button loading={loading} type='primary' icon={<CreditCardOutlined />} onClick={form.submit}>
        Pagar
      </Button>
    </Card>
  );
};

interface PayProcedureProps {
  prcd: States.Procedures;
  banks: States.Banks['banks'];
  institutions: States.Institutions['institutions'];
  auth: States.Auth;
  thm: States.ThemeColors;
  updateProcedure: (data: any, token: string) => Promise<void>;
}

const mapStateToProps = (state: State) => ({
  thm: state.thm,
  prcd: state.prcd,
  banks: state.bk.banks,
  institutions: state.inst.institutions,
  auth: state.auth,
});

export default connect(mapStateToProps, { updateProcedure })(PayProcedure);
